import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useOrganizationContext } from '../organization.context';
import { uploadAsset } from '@/lib/assets';
import { Input } from '@/components/ui/input';
import { updateOrganization } from '@/api/organization.api.private';
import { ASSETS_TYPES } from '@/constants/assets.constants';
import { ORGANIZATIONS_LOGO_URL } from 'shared/constants';

export default function UploadLogoBox() {
  const { setOrganization, organization } = useOrganizationContext();
  const [newImage, setImage] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async event => {
    event.preventDefault();
    setLoading(true);

    try {
      const result = await uploadAsset(newImage, ASSETS_TYPES.ORG_WEB_LOGO);

      const { data, error } = await updateOrganization({
        data: { logo: result.url },
        id: organization.id,
      });

      if (error) {
        throw new Error(error.message || 'Failed to update organization');
      }

      setOrganization(oldOrganization => ({ ...oldOrganization, ...data }));
      toast({
        title: 'Logo uploaded',
        description: 'Logo uploaded successfully',
        status: 'success',
      });

      setImage('');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: 'Error uploading logo',
        description:
          error.message || 'An error occurred while uploading the logo. Please try again.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const setNewLogoPreview = event => {
    const file = event.target.files[0];
    setImage(file);
  };

  const previewSrc = newImage
    ? URL.createObjectURL(newImage)
    : `${ORGANIZATIONS_LOGO_URL}web/${organization.logo}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Logo</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="space-y-2">
            <h3 className="text-lg font-medium">Logo</h3>
            <p className="text-sm text-muted-foreground">
              Logo displayed across the web and mobile app.
            </p>
            <Input
              id="picture"
              type="file"
              name="file"
              accept="image/png, image/jpeg"
              onChange={setNewLogoPreview}
            />
            <div className="mt-4">
              <p className="text-sm font-medium">Preview</p>
              <div className="p-3 rounded-md">
                <img
                  src={previewSrc}
                  alt="Logo preview"
                  width={105}
                  height={105}
                  className="rounded-md"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Button disabled={!newImage} loading={loading} type="submit">
              Upload
            </Button>
            {newImage && organization?.configuration?.hub?.hubLogoUpdatedAt ? (
              <p className="text-sm text-amber-600">This will also override the hub logo.</p>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
